import React, { FC } from 'react';
import TableSimple from 'src/components/TableSimple';
import { CustomRenderType, TableColumnType, TableRowType } from 'src/types/table';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import translate from '@translate';
import { ProductSetProductType } from 'src/types/productSet';
import TextFieldDebounced from 'src/components/TextFieldDebounced';
import ProductsSelect from 'src/components/ProductsSelect';
import { sizes } from 'src/themes/default';
import { FormErrorsType } from '../../types/global';

export type EditKeysType = 'PRODUCT_COUNT' | 'PRODUCT_SKU';

const allColumns: TableColumnType[] = [
    {
        key: 'PRODUCT_SKU',
        label: 'productSku',
        defaultShow: true,
        customRender: true,
    },
    {
        key: 'PRODUCT_COUNT',
        label: 'productCount',
        defaultShow: true,
        customRender: true,
    },
];

type ProductsTablePropsType = {
    newProductSet: boolean;
    data?: ProductSetProductType[];
    onChange: (value: any, error: boolean, errorKey?: string) => void;
    disableEdit?: boolean;
    editErrors: FormErrorsType;
};

const ProductsTable: FC<ProductsTablePropsType> = (props) => {
    const handleRemoveRow = (row: TableRowType) => {
        props.onChange(
            props.data?.filter((item) => item.ID != row.ID),
            false
        );
    };

    const getInputName = (id: number, key: string) => `product-${id}-${key}`;

    const changeValue = (key: EditKeysType, value: any, error: boolean, id: number) => {
        const newData = props.data?.map((product) => {
            if (product.ID === id) {
                const updatedProduct = { ...product };
                updatedProduct[key] = value;
                return updatedProduct;
            }
            return product;
        });
        props.onChange(newData, error, getInputName(id, key));
    };

    const customRender: CustomRenderType = (column, row) => {
        const name = getInputName(row.ID, column.key);
        switch (column.key) {
            case 'PRODUCT_SKU':
                return (
                    <ProductsSelect
                        idKey="SKU"
                        value={row.PRODUCT_SKU}
                        error={props.editErrors[name]}
                        onChange={(value, error) => {
                            if (!Array.isArray(value)) {
                                changeValue('PRODUCT_SKU', value?.SKU, error, row.ID);
                            }
                        }}
                        name={name}
                        fullWidth
                        required
                        disabled={props.disableEdit}
                    />
                );
            case 'PRODUCT_COUNT':
                return (
                    <TextFieldDebounced
                        value={row.PRODUCT_COUNT}
                        error={props.editErrors[name]}
                        onChange={(value, error) =>
                            changeValue('PRODUCT_COUNT', (value || value === 0) && String(value), error, row.ID)
                        }
                        name={name}
                        fullWidth
                        type="numberLocale"
                        min={0}
                        required
                        disabled={props.disableEdit}
                    />
                );
        }
    };

    return (
        <TableSimple
            rowIdKey="ID"
            rows={props.data || []}
            columsLabelPrefix="productSetDetail.productsTable"
            allColumns={allColumns}
            initSort={{ column: 'ID', direction: 'ASC', createIdNumber: true }}
            rowActions={
                props.disableEdit
                    ? undefined
                    : [
                          {
                              icon: <HighlightOffIcon />,
                              label: translate('productSetDetail.action.removeProduct'),
                              onClick: handleRemoveRow,
                              style: { height: sizes.table_action_with_input_H },
                              hiddenFce: () => !!props.disableEdit,
                          },
                      ]
            }
            disableFilter
            customRender={customRender}
            sumCols={{ PRODUCT_COUNT: true }}
            maxHeight={'unset'}
        />
    );
};

export default ProductsTable;
